// Копия витрины: site/src/shared/utils/createPlatformTheme.ts.
// При изменении оригинала синхронизировать этот файл, снимок токенов
// в createPlatformTheme.test.ts ловит расхождение.
//
// Отличия от оригинала (осознанные, см. план
// docs/superpowers/plans/2026-09-16-cover-preview-platform-theme.md):
// 1. Нестандартные поля палитры объявлены необязательными: своя тема
//    кабинета (shared/utils/createTheme.ts) их не задаёт.
// 2. Выброшены MuiPickersPopper (в кабинете свои типы у x-date-pickers)
//    и glassIconButtonSx (нужен только диалогам витрины).
// 3. Выброшен блок MuiCssBaseline (html/body/a/::placeholder): провайдер
//    кабинета оборачивает предпросмотр в ScopedCssBaseline, а не в
//    глобальный CssBaseline витрины, и у него свой ключ темы,
//    MuiScopedCssBaseline, поэтому оверрайды MuiCssBaseline здесь были бы
//    мёртвым кодом.
import {
  createTheme as muiCreateTheme,
  responsiveFontSizes,
  alpha,
  darken,
  lighten,
  rgbToHex,
} from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { CSSProperties } from "react";
import {
  type CurrentPlatformQueryType,
  PlatformThemePaletteTypeType,
} from "@/types/graphql/partner.generated";
import { parseFontFamily } from "./fonts";

declare module "@mui/material/styles" {
  interface TypeBackground {
    body?: string;
    header?: string;
    /** Поверхности над paper: инпуты, плитки счётчика, пустые обложки. */
    elevated?: string;
  }
  interface TypeText {
    primaryInHex?: string;
    /** Подписи, метаданные, плейсхолдеры (макет #8A919C / #5C6470). */
    muted?: string;
  }
  interface TypographyVariants {
    label: CSSProperties;
  }
  interface TypographyVariantsOptions {
    label?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    "outlined-product": true;
    soft: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    badge: true;
    badgeSoft: true;
    success: true;
  }
}

type PlatformTheme = NonNullable<
  NonNullable<CurrentPlatformQueryType["currentPlatform"]>["theme"]
>;

export interface DefaultFontStacks {
  heading: string;
  base: string;
}

// Archivo (the default heading font) has no Cyrillic subset, so the fallback
// must cover non-Latin scripts too, Arial Narrow doesn't.
const HEADING_FALLBACK = '"Helvetica Neue", Arial, sans-serif';
const BASE_FALLBACK =
  'ui-sans-serif, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export function createPlatformTheme(
  platformTheme: PlatformTheme | null | undefined,
  defaultFonts: DefaultFontStacks
): Theme {
  const mode =
    platformTheme?.palette?.type === PlatformThemePaletteTypeType.DarkType
      ? "dark"
      : "light";
  const dark = mode === "dark";

  const backgroundColor =
    platformTheme?.palette?.backgroundColor || (dark ? "#0B0C0F" : "#F4F5F7");
  const paperColor = dark
    ? lighten(backgroundColor, 0.045)
    : darken(backgroundColor, 0.03);
  const elevatedColor = dark
    ? lighten(backgroundColor, 0.08)
    : darken(backgroundColor, 0.06);
  const textColor = dark ? "#FFFFFF" : "#0B0C0F";
  const primaryColor = platformTheme?.palette?.primary || "#2F6FED";
  const secondaryColor = platformTheme?.palette?.secondary || "#6E9BFF";
  const dividerColor = alpha(textColor, dark ? 0.08 : 0.1);
  const headerColor = alpha(textColor, 0.03);

  const headingFont = parseFontFamily(
    platformTheme?.typography?.headingFontFamily
  );
  const baseFont = parseFontFamily(platformTheme?.typography?.fontFamily);
  const headingFontFamily = headingFont
    ? `${headingFont.stack}, ${HEADING_FALLBACK}`
    : `${defaultFonts.heading}, ${HEADING_FALLBACK}`;
  const baseFontFamily = baseFont
    ? `${baseFont.stack}, ${BASE_FALLBACK}`
    : `${defaultFonts.base}, ${BASE_FALLBACK}`;

  const heading = (weight: number) => ({
    fontFamily: headingFontFamily,
    fontWeight: weight,
  });

  const theme = muiCreateTheme({
    palette: {
      mode,
      primary: { main: primaryColor, light: lighten(primaryColor, 0.18) },
      secondary: { main: secondaryColor },
      success: { main: dark ? "#57D98A" : "#1F9D55" },
      divider: dividerColor,
      background: {
        body: backgroundColor,
        default: backgroundColor,
        paper: paperColor,
        elevated: elevatedColor,
        header: headerColor,
      },
      text: {
        primary: textColor,
        secondary: alpha(textColor, dark ? 0.72 : 0.65),
        muted: alpha(textColor, 0.45),
        primaryInHex: rgbToHex(textColor),
      },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: baseFontFamily,
      h1: {
        ...heading(900),
        textTransform: "uppercase",
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
      },
      h2: {
        ...heading(900),
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        lineHeight: 1.15,
      },
      h3: { ...heading(900), lineHeight: 1.15 },
      h4: heading(800),
      h5: heading(800),
      h6: { ...heading(800), lineHeight: 1.35 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontWeight: 500, lineHeight: 1.65 },
      body2: { fontWeight: 500, lineHeight: 1.5 },
      caption: { fontWeight: 500 },
      button: {
        ...heading(800),
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      },
      overline: {
        ...heading(700),
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        lineHeight: 1.6,
      },
      label: {
        ...heading(700),
        fontSize: "0.75rem",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        lineHeight: 1.4,
      },
    },
  });

  const p = theme.palette;
  const primaryShadow = `0 8px 24px ${alpha(p.primary.main, 0.35)}`;
  const softBorder = alpha(p.text.primary, 0.2);

  const fullTheme = muiCreateTheme(theme, {
    components: {
      MuiTypography: {
        defaultProps: { variantMapping: { label: "div" } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, textTransform: "uppercase" },
          sizeSmall: { padding: "8px 16px", fontSize: "0.75rem" },
          sizeMedium: { padding: "12px 24px", fontSize: "0.8125rem" },
          sizeLarge: { padding: "16px 32px", fontSize: "0.9375rem" },
          containedPrimary: {
            boxShadow: primaryShadow,
            "&:hover": {
              backgroundColor: p.primary.light,
              boxShadow: primaryShadow,
            },
          },
          outlined: {
            borderColor: softBorder,
            "&:hover": {
              borderColor: alpha(p.text.primary, 0.45),
              backgroundColor: alpha(p.text.primary, 0.06),
            },
          },
          text: { "&:hover": { backgroundColor: alpha(p.text.primary, 0.06) } },
        },
        variants: [
          {
            props: { variant: "soft" },
            style: {
              color: p.text.primary,
              backgroundColor: alpha(p.text.primary, 0.1),
              border: `1px solid ${softBorder}`,
              "&:hover": { backgroundColor: alpha(p.text.primary, 0.18) },
            },
          },
          {
            props: { variant: "outlined-product" },
            style: {
              border: "4px solid",
              borderColor: p.primary.main,
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "transparent" },
            },
          },
        ],
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: p.background.paper,
            backgroundImage: "none",
          },
          rounded: { borderRadius: 14 },
          outlined: { borderColor: p.divider },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0, variant: "outlined" },
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: p.background.paper,
            borderColor: p.divider,
            "&.MuiPaper-root": { backgroundColor: p.background.paper },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: "0.78rem",
            borderRadius: 999,
          },
          // Только чип без цвета (color="default", то есть большинство
          // "тегов") красится в paper с рамкой; цветные (color="primary" и
          // прочие) остаются на заливке MUI, иначе contrastText теряет
          // контраст с собственным фоном.
          colorDefault: {
            backgroundColor: p.background.paper,
            border: `1px solid ${p.divider}`,
          },
        },
        variants: [
          {
            props: { variant: "badge" },
            style: {
              height: 24,
              borderRadius: 6,
              border: "none",
              backgroundColor: p.primary.main,
              color: p.primary.contrastText,
              fontFamily: headingFontFamily,
              fontWeight: 800,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            },
          },
          {
            props: { variant: "badgeSoft" },
            style: {
              height: 24,
              borderRadius: 6,
              border: "none",
              backgroundColor: alpha(p.text.primary, 0.12),
              backdropFilter: "blur(4px)",
              color: p.text.primary,
              fontFamily: headingFontFamily,
              fontWeight: 700,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            },
          },
          {
            props: { variant: "success" },
            style: {
              height: 26,
              borderRadius: 6,
              border: "none",
              backgroundColor: alpha(p.success.main, 0.14),
              color: p.success.main,
              fontFamily: headingFontFamily,
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            },
          },
        ],
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, backgroundColor: p.primary.main },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: headingFontFamily,
            fontWeight: 700,
            fontSize: "0.8125rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            minWidth: "0 !important",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: p.background.default,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: p.divider },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: softBorder,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: p.primary.main,
              borderWidth: 1,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: alpha(p.text.primary, 0.3),
            "& .MuiSvgIcon-root": { fontSize: 22, borderRadius: 6 },
            "&.Mui-checked": { color: p.primary.main },
          },
        },
      },
      MuiModal: {
        styleOverrides: { backdrop: { backgroundColor: "rgba(0,0,0,0.5)" } },
      },
      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: "rgba(0, 0, 0, 0.9)" } },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: "none", backgroundColor: headerColor },
          colorDefault: { backgroundColor: headerColor },
          colorPrimary: { backgroundColor: headerColor },
          colorSecondary: { backgroundColor: headerColor },
          colorInherit: { backgroundColor: headerColor },
        },
      },
      MuiDialog: {
        defaultProps: { PaperProps: { elevation: 0 } },
        styleOverrides: { paper: { backgroundColor: paperColor } },
      },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: paperColor } } },
      MuiSnackbarContent: { styleOverrides: { root: { color: "white" } } },
      MuiListItemButton: {
        styleOverrides: {
          root: { "&.Mui-selected": { backgroundColor: paperColor } },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { "&:focus": { background: "transparent" } },
        },
      },
      MuiSvgIcon: { styleOverrides: { root: { touchAction: "none" } } },
      MuiPopover: {
        styleOverrides: { paper: { backgroundColor: backgroundColor } },
      },
      MuiAutocomplete: {
        defaultProps: { componentsProps: { paper: { elevation: 2 } } },
        styleOverrides: {
          paper: {
            backgroundColor: backgroundColor,
            backdropFilter: "blur(10px)",
            boxShadow:
              "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
          },
        },
      },
    },
  });

  return responsiveFontSizes(fullTheme, {
    variants: ["h1", "h2", "h3", "h4", "h5", "h6"],
  });
}
